// 🔴 LE PARCOURS DE CRÉATION **CORE** — le socle OmniHub, ⛔ AUCUN métier dedans.
//
// ⚠️ POURQUOI IL EXISTE. Mesuré : le modèle `Dossier` a UN SEUL champ obligatoire
// (`activite`) et ⛔ AUCUN champ TEF requis — il EST déjà générique. Ce qui ⛔ ne
// l'était pas, c'est l'ÉCRAN : le wizard demandait GIR, RFR, statut d'occupation
// et « MaPrimeAdapt' » à TOUT tenant qui n'était pas OMV.
// 🔑 ⇒ On ⛔ ne crée PAS `DossierMario`. On ouvre le CORE tel qu'il est : client,
// site, contact. Les métiers ajoutent ENSUITE leurs extensions — pour Mario,
// FUITE et DEVIS, qui vivent dans `visite_profils`.
//
// 🔴 CE QU'IL ⛔ NE DEMANDE JAMAIS : MaPrimeAdapt' · ANAH · GIR · mandataire ·
// régime d'aides · RFR · statut d'occupation. Si un métier en a besoin un jour,
// il le DÉCLARE — ⛔ il ne l'hérite pas.
import React, { useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";

// 🔑 DEUX PRIMITIVES CORE, RÉUTILISÉES — ⛔ pas réécrites pour Mario.
// `BanAddressInput` existait (le wizard TEF s'en sert déjà) ; la recherche
// d'entreprise existait DUPLIQUÉE dans 3 écrans, extraite en primitive.
import BanAddressInput from "../../components/shared/BanAddressInput";
import EntrepriseSearchInput from "../../components/shared/EntrepriseSearchInput";
import { hasDossierSite } from "../../utils/tenantProfile";
// 🔴 N°797 — LE TYPE DE CLIENT VIENT DU TENANT, ⛔ plus de deux boutons en dur.
//    MESURÉ : cet écran (« OH-CREATION-CORE ») proposait « Particulier / Entreprise »,
//    donc ⛔ NI Syndic ⛔ NI Professionnel, alors que `mario_bat` déclare les trois
//    depuis la migration 064. Deux écrans lisaient déjà l'autorité, celui-ci ⛔ non :
//    c'est ce qui faisait voir DEUX listes différentes selon l'écran.
import { useConfigList } from "../../hooks/useConfigList";
// 🔴 P0 SYNDIC — la NATURE du type (personne morale ou non) vient de la source
//    unique partagée avec la fiche client (`utils/clientFormat`, sur B2B_TYPES
//    contient `professionnel` ET `copropriete`). ⛔ Aucune liste de types ici,
//    ⛔ aucun test de tenant.
import { estPersonneMorale, normaliserAdresse } from "../../utils/clientFormat";

/** « SIRET ou SIREN » saisi ⇒ {siret, siren} ; ⛔ jamais une valeur devinée.
 *  14 chiffres = SIRET (son SIREN = ses 9 premiers) ; 9 chiffres = SIREN. */
export function identifiantLegal(saisie) {
  const n = String(saisie || "").replace(/\s/g, "");
  if (!n) return { siret: undefined, siren: undefined };
  if (/^\d{14}$/.test(n)) return { siret: n, siren: n.slice(0, 9) };
  if (/^\d{9}$/.test(n)) return { siret: undefined, siren: n };
  return null;                                   // format illisible ⇒ refus explicite
}

export const MARQUEUR_CREATION_CORE = "OH-CREATION-CORE";

export default function CreateDossierCore({ tenantId, activite, onBack,
                                            onSuccess }) {
  // 🔴 PARTICULIER | ENTREPRISE — déclaré par l'utilisateur, ⛔ pas déduit.
  const [type, setType] = useState("particulier");
  // 🔑 SOURCE UNIQUE : `metier_config_listes{tenant_id, type:"client_types"}`.
  // ⚠️ ⛔ AUCUN repli enrichi ici — c'est le doublon qu'on ferme (N°797). Si la liste
  //    n'est pas seedée, on garde les deux valeurs HISTORIQUES de cet écran pour ⛔ ne
  //    pas le rendre inutilisable, et ⛔ on ⛔ n'y ajoute PAS Syndic : une valeur
  //    nouvelle entre par la liste du tenant, ⛔ jamais par le code.
  const { items: typesClient } = useConfigList("client_types");
  const optionsType = typesClient.length
    ? typesClient.map((o) => [o.id, o.label])
    : [["particulier", "Particulier"], ["professionnel", "Entreprise"]];
  const [client, setClient] = useState({ nom: "", prenom: "", telephone: "",
                                         email: "", siret: "", siren: "" });
  // 🔴 P0 SYNDIC — l'identifiant légal SAISI (SIRET ou SIREN), visible et
  //    modifiable ; rempli aussi par le choix d'une entreprise au registre.
  const [identifiant, setIdentifiant] = useState("");
  const morale = estPersonneMorale(type);
  const [site, setSite] = useState({ adresse: "", code_postal: "", ville: "" });
  const siteRequis = hasDossierSite();
  const [notes, setNotes] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);

  // 🔴 L'ACTIVITÉ VIENT DU TENANT, DÉCLARÉE — ⛔ jamais devinée ici.
  // Sans elle, on refuse : créer un dossier sans savoir de quel métier il
  // relève, c'est exactement ce qu'on vient de supprimer.
  if (!activite) {
    return (
      <div style={{ background: "var(--oh-bg-elevated)",
                    border: "0.5px solid var(--oh-border)",
                    borderRadius: 12, padding: 16 }}
           data-testid="creation-core-sans-activite">
        <div style={{ color: "var(--oh-text)", fontWeight: 500, marginBottom: 8 }}>
          Activité du dossier non configurée
        </div>
        <div style={{ color: "var(--oh-text-secondary)", fontSize: 13,
                      lineHeight: 1.6 }}>
          Ce métier a un parcours de création, mais l'activité que portent ses
          dossiers n'est pas déclarée (<code>config.dossier_activite</code>).
          Aucune activité n'est choisie à sa place.
        </div>
      </div>
    );
  }

  // 🔑 MÊME IDIOME QUE LES AUTRES ÉCRANS — ⛔ mais SANS le repli `|| "tef"`
  // qu'ils portent (CreateDossierTefWizard l.73) : donner le tenant d'un AUTRE
  // métier faute de tenant courant est exactement ce qu'on vient de supprimer.
  const entetes = () => {
    const jt = localStorage.getItem("session_token");
    return { "X-Tenant-ID": tenantId, "Content-Type": "application/json",
             ...(jt && { Authorization: `Bearer ${jt}` }) };
  };

  const creer = async () => {
    setErreur(null);
    setOccupe(true);
    try {
      // 🔴 P0 SYNDIC / ENTREPRISE — ⛔ on ne perd NI le nom NI l'identifiant légal.
      let legal = { siret: undefined, siren: undefined };
      if (morale) {
        legal = identifiantLegal(identifiant);
        if (legal === null) {
          throw new Error("SIRET (14 chiffres) ou SIREN (9 chiffres) illisible.");
        }
        // Un SIRET/SIREN tapé dans le champ NOM sans choisir l'entreprise proposée
        // serait enregistré COMME NOM (mesuré) : on refuse et on le DIT.
        if (identifiantLegal(client.nom) && /^\d+$/.test(client.nom.replace(/\s/g, ""))) {
          throw new Error("Le nom ne peut pas être un SIRET/SIREN : choisissez "
                          + "l'entreprise proposée ou saisissez son nom.");
        }
      }
      // ── 1 · LE CLIENT — par l'API normale, ⛔ jamais en base.
      const rc = await fetch("/api/metier/clients", {
        method: "POST",
        headers: entetes(),
        body: JSON.stringify({
          nom: client.nom.trim(),
          prenom: (!morale && client.prenom.trim()) || undefined,
          telephone: client.telephone.trim() || undefined,
          email: client.email.trim() || undefined,
          type_client: type,
          // personne morale : le nom EST la raison sociale (même règle que la fiche client)
          raison_sociale: morale ? client.nom.trim() : undefined,
          siret: morale ? legal.siret : undefined,
          siren: morale ? legal.siren : undefined,
        }),
      });
      const jc = await rc.json().catch(() => ({}));
      if (!rc.ok) {
        // ⚠️ Le refus de doublon est un RÉSULTAT, ⛔ pas un obstacle : on le
        // MONTRE tel quel. ⛔ Jamais de `force=true` pour passer outre.
        throw new Error(jc?.detail?.message || jc?.detail
                        || `Création du client refusée (${rc.status})`);
      }
      const clientId = jc.id || jc?.client?.id;

      // 🔴 L2 (N°876) — tenant qui a ADOPTÉ le modèle Site : le lieu saisi devient
      // un SITE du client, et le dossier le reçoit (`site_id`). ⛔ Aucun site
      // inventé : sans adresse ni ville, le bouton « Créer » reste inactif.
      // ⚠️ Pour les autres tenants, rien ne change (cf. dette : `adresse_travaux`
      // n'est pas un champ de `DossierCreate` et est ignoré côté serveur).
      // 🔴 N°915-A — CONTRAT CORE : `adresse` = la VOIE SEULE. `onPick` (BAN) rend déjà
      //    la rue découpée, mais la SAISIE LIBRE et le collage donnent « rue, CP ville » —
      //    stocké tel quel AVEC `code_postal`/`ville` à côté, tout rendu affichait alors
      //    le CP et la ville DEUX FOIS (N°914-A). ⛔ Pas de découpage local : la fonction
      //    Core `normaliserAdresse` (idempotente) est la seule.
      const siteNorm = normaliserAdresse(site);
      let siteId;
      if (siteRequis) {
        const rs = await fetch(`/api/metier/clients/${encodeURIComponent(clientId)}/sites`, {
          method: "POST",
          headers: entetes(),
          body: JSON.stringify({ adresse: siteNorm.adresse, ville: siteNorm.ville,
                                 code_postal: siteNorm.code_postal || undefined }),
        });
        const js = await rs.json().catch(() => ({}));
        if (!rs.ok) {
          throw new Error(js?.detail || `Création du site refusée (${rs.status})`);
        }
        siteId = js.id;
      }

      // ── 2 · LE DOSSIER CORE — activité DÉCLARÉE, ⛔ aucun champ métier.
      const rd = await fetch("/api/metier/dossiers", {
        method: "POST",
        headers: entetes(),
        body: JSON.stringify({
          client_id: clientId,
          ...(siteId ? { site_id: siteId } : {}),
          activite,
          adresse_travaux: siteNorm.adresse || undefined,
          code_postal_travaux: siteNorm.code_postal || undefined,
          ville_travaux: siteNorm.ville || undefined,
          notes: notes.trim() || undefined,
        }),
      });
      const jd = await rd.json().catch(() => ({}));
      if (!rd.ok) {
        throw new Error(jd?.detail?.message || jd?.detail
                        || `Création du dossier refusée (${rd.status})`);
      }
      onSuccess && onSuccess(jd.dossier || jd);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setOccupe(false);
    }
  };

  const champ = (v, set, ph, tid, htmlType = "text") => (
    <input value={v} onChange={(e) => set(e.target.value)} placeholder={ph}
           type={htmlType} aria-label={ph} data-testid={tid}
           style={{ height: 36, borderRadius: 8, padding: "0 10px",
                    border: "1px solid var(--oh-border)",
                    background: "var(--oh-bg-surface)", color: "var(--oh-text)",
                    width: "100%" }} />
  );

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}
         data-testid="creation-core">
      <div style={{ display: "flex", alignItems: "center", gap: 12,
                    marginBottom: 16 }}>
        {onBack && (
          <button onClick={onBack} aria-label="Retour"
                  style={{ background: "transparent", border: "none",
                           color: "var(--oh-text-secondary)" }}>
            <ArrowLeft size={18} />
          </button>
        )}
        <div style={{ color: "var(--oh-text)", fontWeight: 500, fontSize: 18 }}>
          Nouveau dossier
        </div>
        <span style={{ fontSize: 10, opacity: 0.35, marginLeft: "auto" }}>
          {MARQUEUR_CREATION_CORE}
        </span>
      </div>

      {erreur && (
        <div style={{ background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)", borderRadius: 8,
                      padding: 10, marginBottom: 12, fontSize: 13 }}
             data-testid="creation-core-erreur">{erreur}</div>
      )}

      <div style={{ background: "var(--oh-bg-elevated)",
                    border: "0.5px solid var(--oh-border)", borderRadius: 12,
                    padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 500, marginBottom: 12 }}>Client</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {optionsType.map(([v, lbl]) => (
            <button key={v} type="button" onClick={() => setType(v)}
                    data-testid={`core-type-${v}`}
                    style={{ flex: 1, height: 36, borderRadius: 8,
                             border: "1px solid " + (type === v
                               ? "var(--oh-accent)" : "var(--oh-border)"),
                             background: type === v
                               ? "var(--oh-accent-bg)" : "transparent",
                             color: type === v
                               ? "var(--oh-accent-text)" : "var(--oh-text-secondary)" }}>
              {lbl}
            </button>
          ))}
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {morale && (
            <EntrepriseSearchInput value={client.nom} testId="core-entreprise"
              placeholder={type === "copropriete" ? "Nom du syndic (ou SIRET à rechercher)"
                                                  : "Nom de l'entreprise (ou SIRET à rechercher)"}
              onChange={(v) => setClient({ ...client, nom: v })}
              onPick={(e) => { setClient({ ...client, nom: e.nom,
                                           siret: e.siret, siren: e.siren });
                               setIdentifiant(e.siret || e.siren || "");
                               if (e.adresse) setSite({ adresse: e.adresse,
                                 code_postal: e.code_postal, ville: e.ville }); }} />
          )}
          {morale && champ(identifiant, setIdentifiant, "SIRET ou SIREN",
                           "core-client-identifiant")}
          {type === "particulier"
            && champ(client.nom, (v) => setClient({ ...client, nom: v }),
                     "Nom", "core-client-nom")}
          {type === "particulier"
            && champ(client.prenom, (v) => setClient({ ...client, prenom: v }),
                     "Prénom", "core-client-prenom")}
          {champ(client.telephone, (v) => setClient({ ...client, telephone: v }),
                 "Téléphone", "core-client-tel", "tel")}
          {champ(client.email, (v) => setClient({ ...client, email: v }),
                 "Email", "core-client-email", "email")}
        </div>
      </div>

      <div style={{ background: "var(--oh-bg-elevated)",
                    border: "0.5px solid var(--oh-border)", borderRadius: 12,
                    padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 500, marginBottom: 12 }}>
          Site de l'intervention
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          <BanAddressInput value={site.adresse} testId="core-site-adresse"
            placeholder="Adresse d'intervention (autocomplétée)"
            onChange={(v) => setSite({ ...site, adresse: v })}
            onPick={({ rue, cp, ville }) => setSite({
              adresse: rue, code_postal: cp || site.code_postal,
              ville: ville || site.ville })} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr",
                        gap: 8 }}>
            {champ(site.code_postal, (v) => setSite({ ...site, code_postal: v }),
                   "Code postal", "core-site-cp")}
            {champ(site.ville, (v) => setSite({ ...site, ville: v }),
                   "Ville", "core-site-ville")}
          </div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes (facultatif)" rows={2}
                    aria-label="Notes" data-testid="core-notes"
                    style={{ borderRadius: 8, padding: 10,
                             border: "1px solid var(--oh-border)",
                             background: "var(--oh-bg-surface)",
                             color: "var(--oh-text)", width: "100%" }} />
        </div>
      </div>

      <button onClick={creer} disabled={!client.nom.trim() || occupe
                                          || (siteRequis && !(site.adresse.trim() && site.ville.trim()))}
              data-testid="core-creer"
              style={{ height: 40, borderRadius: 8, width: "100%",
                       border: "none", fontWeight: 500,
                       background: client.nom.trim() && !occupe
                         ? "var(--oh-accent-solid)" : "var(--oh-bg-surface)",
                       color: client.nom.trim() && !occupe
                         ? "var(--oh-accent-contrast)" : "var(--oh-text-muted)" }}>
        {occupe ? <Loader2 size={16} className="animate-spin" />
                : "Créer le dossier"}
      </button>
      <div style={{ color: "var(--oh-text-secondary)", fontSize: 12,
                    marginTop: 10, lineHeight: 1.6 }}>
        Activité : <strong>{activite}</strong> — déclarée par votre
        organisation. Les interventions (visites, rapports, devis) se lancent
        ensuite depuis le dossier.
      </div>
    </div>
  );
}
